/**
 * The console's bindings of the governed audit projections (#2444 phase 4).
 *
 * Every call goes through `@solum/sdk`'s audit module — the ONE place the
 * projection names, arguments and columns live — over `client.raw()`. This
 * file adds only the query keys, the paging and the CSV columns; it holds no
 * function name of its own, so a server signature change never reaches it.
 *
 * Authorization is the SERVER's. `core.is_audit_reader()` says whether the
 * pages should be offered, and every projection refuses (42501) a caller who
 * is not one, which `queryState` classifies as `unauthorized`. Nothing here
 * decides who is an administrator.
 */
import { useInfiniteQuery, useQuery } from '@tanstack/react-query'
import {
  auditCsv, auditExport, auditSecurityPosture, auditTimeline, correlationTrace, isAuditReader,
  myToolInvocations, recordHistory, recordLastChange, toolInvocationView, trustPresentation,
  type ToolOutcome,
  type AuditTimelineFilter, type AuditTimelineRow, type ToolInvocationFilter, type ToolInvocationRow,
} from '@solum/sdk'
import { useSolum } from '../data/client'
import { actionWords, formatDuration, formatWhen, outcomePresentation, surfaceLabel, type Zone } from './auditWords'

type Client = ReturnType<typeof useSolum>['client']

/** Whether the caller may read the audit pages — asked once, cached. */
export function useIsAuditReader() {
  const { client } = useSolum()
  return useQuery({ queryKey: ['audit', 'is-reader'], queryFn: () => isAuditReader(client.raw()) })
}

/** The latest authorized change to one record, for its System information. */
export function useRecordLastChange(entity: string, id: string | null) {
  const { client } = useSolum()
  return useQuery({
    queryKey: ['audit', 'last-change', entity, id],
    queryFn: () => recordLastChange(client.raw(), entity, id!),
    enabled: Boolean(id),
  })
}

/**
 * ONE record's change history, for the drawer Timeline's Changes lane
 * (`core.record_history`). Labels and the server-derived detail only; nothing
 * for a record the caller cannot see. The key is exported because a write on
 * the record (share, revoke, reassign) must refresh exactly this query.
 */
export const recordHistoryKey = (entity: string, id: string | null) => ['audit', 'record-history', entity, id] as const

export function useRecordHistory(entity: string, id: string | null, enabled = true) {
  const { client } = useSolum()
  return useQuery({
    queryKey: recordHistoryKey(entity, id),
    queryFn: () => recordHistory(client.raw(), entity, id!),
    enabled: enabled && Boolean(id),
  })
}

/** The caller's OWN tool calls (`core.my_tool_invocations`) — never anyone else's, never the arguments. */
export function useMyToolInvocations(outcomes?: readonly ToolOutcome[], limit = 40) {
  const { client } = useSolum()
  return useQuery({
    queryKey: ['audit', 'my-tools', outcomes ?? null, limit],
    queryFn: () => myToolInvocations(client.raw(), { outcomes, limit }),
  })
}

export function useAuditTimeline(filter: AuditTimelineFilter, enabled = true) {
  const { client } = useSolum()
  return useInfiniteQuery({
    queryKey: ['audit', 'timeline', filter],
    queryFn: ({ pageParam }) => auditTimeline(client.raw(), filter, pageParam),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.nextCursor,
    enabled,
  })
}

export function useToolInvocations(filter: ToolInvocationFilter, enabled = true) {
  const { client } = useSolum()
  return useInfiniteQuery({
    queryKey: ['audit', 'tools', filter],
    queryFn: ({ pageParam }) => toolInvocationView(client.raw(), filter, pageParam),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.nextCursor,
    enabled,
  })
}

export function useCorrelationTrace(correlationId: string | null) {
  const { client } = useSolum()
  return useQuery({
    queryKey: ['audit', 'trace', correlationId],
    queryFn: () => correlationTrace(client.raw(), correlationId!),
    enabled: correlationId !== null,
  })
}

export function useSecurityPosture(enabled = true) {
  const { client } = useSolum()
  return useQuery({ queryKey: ['audit', 'posture'], queryFn: () => auditSecurityPosture(client.raw()), enabled })
}

/* ── export ─────────────────────────────────────────────────────────────────
 * The rows come from the export doors (same projection, same authorization,
 * audited server-side as `audit.exported`); this side only formats. Every
 * column is words or a label — the correlation id is the one key, kept so
 * support can join a line of the file back to its trace. */

const who = (label: string | null, onBehalfOf: string | null) =>
  label ? (onBehalfOf ? `${label} on behalf of ${onBehalfOf}` : label) : 'System'

const timelineColumns = (zone: Zone) => [
  { header: 'When', value: (r: AuditTimelineRow) => formatWhen(r.at, zone) },
  { header: 'Action', value: (r: AuditTimelineRow) => actionWords(r.action) },
  { header: 'Entity', value: (r: AuditTimelineRow) => r.entityLabel },
  { header: 'Record', value: (r: AuditTimelineRow) => r.recordLabel },
  { header: 'Actor', value: (r: AuditTimelineRow) => who(r.actorLabel, r.onBehalfOfLabel) },
  { header: 'Account', value: (r: AuditTimelineRow) => r.accountLabel },
  { header: 'Source', value: (r: AuditTimelineRow) => r.sourceLabel },
  { header: 'Trust', value: (r: AuditTimelineRow) => trustPresentation(r.sourceTrust).label },
  { header: 'Client', value: (r: AuditTimelineRow) => r.clientLabel },
  { header: 'Correlation', value: (r: AuditTimelineRow) => r.correlationId },
]

const toolColumns = (zone: Zone) => [
  { header: 'When', value: (r: ToolInvocationRow) => formatWhen(r.at, zone) },
  { header: 'Tool', value: (r: ToolInvocationRow) => r.toolLabel },
  { header: 'Outcome', value: (r: ToolInvocationRow) => outcomePresentation(r.outcome).label },
  { header: 'Duration', value: (r: ToolInvocationRow) => formatDuration(r.durationMs) },
  { header: 'Surface', value: (r: ToolInvocationRow) => surfaceLabel(r.surface) },
  { header: 'Actor', value: (r: ToolInvocationRow) => who(r.actorLabel, r.onBehalfOfLabel) },
  { header: 'Source', value: (r: ToolInvocationRow) => r.sourceLabel },
  { header: 'Trust', value: (r: ToolInvocationRow) => trustPresentation(r.sourceTrust).label },
  { header: 'Client', value: (r: ToolInvocationRow) => r.clientLabel },
  { header: 'Correlation', value: (r: ToolInvocationRow) => r.correlationId },
]

export async function exportTimelineCsv(client: Client, filter: AuditTimelineFilter, zone: Zone): Promise<string> {
  return auditCsv(timelineColumns(zone), await auditExport(client.raw(), 'timeline', filter))
}

export async function exportToolsCsv(client: Client, filter: ToolInvocationFilter, zone: Zone): Promise<string> {
  return auditCsv(toolColumns(zone), await auditExport(client.raw(), 'tools', filter))
}

/** The file name: what was exported and over which days. */
export function exportFileName(kind: 'timeline' | 'tools', filter: { from: string; to: string }): string {
  const day = (iso: string) => iso.slice(0, 10)
  return `audit-${kind === 'timeline' ? 'timeline' : 'tool-invocations'}_${day(filter.from)}_${day(filter.to)}.csv`
}

/** Hands the file to the browser. A no-op where there is no object-URL support (tests). */
export function downloadCsv(name: string, csv: string): void {
  if (typeof document === 'undefined' || typeof URL.createObjectURL !== 'function') return
  const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }))
  const a = document.createElement('a')
  a.href = url
  a.download = name
  a.click()
  URL.revokeObjectURL(url)
}

/** What a failed export says. A refusal and "too many rows" are named; the rest is generic. */
export function exportFailureWords(error: unknown): string {
  const msg = error instanceof Error ? error.message : String(error ?? '')
  if (/\b(401|403)\b|42501|permission denied|not authorized/i.test(msg)) return 'You are not permitted to export the audit history.'
  if (/22023|narrow|10[, ]?000/i.test(msg)) return 'More than 10,000 rows match. Narrow the filter or the date range, then export again.'
  return 'The export could not be produced. Try again — if it keeps happening, tell your administrator.'
}
